/**
 * @sazeo/sdk — Tasks hooks
 *
 * Minimal hooks that call the `tasks` Edge Function. All requests carry the
 * caller's Supabase JWT. RLS enforces tenant isolation.
 *
 * Paired spec: specs/CODEX_PHASE2_WAVE4_CALENDAR_TASKS_HOME.md §2.5 / §3.2
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskTagRow {
  id: string;
  tenant_id: string;
  name: string;
  color: string | null;
}

export interface TaskRow {
  id: string;
  tenant_id: string;
  created_by: string;
  assignee_id: string | null;
  title: string;
  description: string | null;
  source_lang: string;
  status: TaskStatus;
  priority: TaskPriority;
  due_at: string | null;
  completed_at: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  created_at: string;
  updated_at: string;
  // Optional embeds returned by the Edge Function
  assignee?: { user_id: string; display_name: string | null; avatar_url: string | null } | null;
  tags?: TaskTagRow[];
}

export interface TaskFilter {
  status?: TaskStatus;
  priority?: TaskPriority;
  assignee?: string | 'me' | 'any';
  tag?: string;
  due_before?: string;
  due_after?: string;
}

export interface TaskCreateInput {
  title: string;
  description?: string | null;
  assignee_id?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  due_at?: string | null;
  related_entity_type?: string | null;
  related_entity_id?: string | null;
  tag_ids?: string[];
}

export type TaskUpdateInput = Partial<TaskCreateInput>;

// ---------------------------------------------------------------------------
// Internal fetch helpers
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return {
      auth: {
        getSession: async () => ({ data: { session: null } }),
      },
    };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

async function apiDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, { method: 'DELETE', headers });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function buildFilterQuery(filter: TaskFilter | undefined): string {
  if (!filter) return '';
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.priority) params.set('priority', filter.priority);
  if (filter.assignee) params.set('assignee', filter.assignee);
  if (filter.tag) params.set('tag', filter.tag);
  if (filter.due_before) params.set('due_before', filter.due_before);
  if (filter.due_after) params.set('due_after', filter.due_after);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useTasks(filter?: TaskFilter) {
  const keyParts: readonly unknown[] = [
    filter?.status ?? null,
    filter?.priority ?? null,
    filter?.assignee ?? null,
    filter?.tag ?? null,
    filter?.due_before ?? null,
    filter?.due_after ?? null,
  ];
  return useAsyncQuery<TaskRow[]>(
    () => apiFetch<TaskRow[]>(`tasks${buildFilterQuery(filter)}`),
    [],
    keyParts,
  );
}

export function useTask(taskId: string | null) {
  return useAsyncQuery<TaskRow | null>(
    () => (taskId ? apiFetch<TaskRow>(`tasks/${taskId}`) : Promise.resolve(null)),
    null,
    [taskId],
    Boolean(taskId),
  );
}

export function useCreateTask() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: TaskCreateInput) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<TaskRow>('tasks', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error };
}

export function useUpdateTask() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { task_id: string; patch: TaskUpdateInput }) => {
    setIsPending(true);
    try {
      return await apiFetch<TaskRow>(`tasks/${input.task_id}`, {
        method: 'PATCH',
        body: JSON.stringify(input.patch),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useDeleteTask() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (taskId: string) => {
    setIsPending(true);
    try {
      await apiDelete(`tasks/${taskId}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useToggleTaskStatus() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { task_id: string; status: TaskStatus }) => {
    setIsPending(true);
    try {
      return await apiFetch<TaskRow>(`tasks/${input.task_id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: input.status }),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export function useTaskTags() {
  return useAsyncQuery<TaskTagRow[]>(() => apiFetch<TaskTagRow[]>('tasks/tags'), [], []);
}

export function useCreateTaskTag() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { name: string; color?: string | null }) => {
    setIsPending(true);
    try {
      return await apiFetch<TaskTagRow>('tasks/tags', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useAssignTag() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { task_id: string; tag_id: string }) => {
    setIsPending(true);
    try {
      await apiFetch<void>(`tasks/${input.task_id}/tags/${input.tag_id}`, {
        method: 'POST',
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useUnassignTag() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { task_id: string; tag_id: string }) => {
    setIsPending(true);
    try {
      await apiDelete(`tasks/${input.task_id}/tags/${input.tag_id}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}
